/*
  Upsell product rows (product page "Pairs well with", cart drawer, cart page).
  - Picking a variant updates the price and what the Add button will add.
  - Product page rows have a quantity stepper; Add and the bundle use that quantity.
  - Add posts to /cart/add.js, then asks the theme to refresh the cart (cart:refresh),
    which updates the cart count, the drawer and the cart page, and opens the drawer.
*/
if (!customElements.get('m-upsell-list')) {
  class MUpsellList extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    get isCartPage() {
      return window.DeliSettings && DeliSettings.templateName === 'cart';
    }

    onChange = (event) => {
      const qty = event.target.closest('[data-upsell-qty]');
      if (qty) {
        qty.value = Math.min(99, Math.max(1, parseInt(qty.value, 10) || 1));
        return;
      }
      const select = event.target.closest('[data-upsell-variant]');
      if (!select) return;

      const item = select.closest('[data-upsell-item]');
      const option = select.options[select.selectedIndex];
      const button = item.querySelector('[data-upsell-add]');
      const price = item.querySelector('[data-upsell-price]');
      const compare = item.querySelector('[data-upsell-compare]');
      if (!option) return;

      if (price && option.dataset.price) price.textContent = option.dataset.price;
      if (compare) {
        if (option.dataset.compare) {
          compare.textContent = option.dataset.compare;
          compare.removeAttribute('hidden');
        } else {
          compare.setAttribute('hidden', '');
        }
      }
      if (!button) return;

      const available = option.dataset.available === 'true';
      const label = button.querySelector('[data-upsell-add-label]');
      button.dataset.variantId = select.value;
      button.disabled = !available;
      if (label) label.textContent = available ? button.dataset.addLabel : button.dataset.soldOutLabel;
    };

    rowQuantity(el) {
      const row = el.closest('[data-upsell-item]');
      const input = row && row.querySelector('[data-upsell-qty]');
      return Math.min(99, Math.max(1, parseInt(input && input.value, 10) || 1));
    }

    onClick = async (event) => {
      // Quantity stepper (product page rows). Fires change so the bundle total updates.
      const step = event.target.closest('[data-upsell-qty-step]');
      if (step) {
        const input = step.parentElement.querySelector('[data-upsell-qty]');
        if (!input) return;
        const next = (parseInt(input.value, 10) || 1) + Number(step.dataset.upsellQtyStep);
        input.value = Math.min(99, Math.max(1, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const button = event.target.closest('[data-upsell-add]');
      if (!button || button.disabled) return;

      const label = button.querySelector('[data-upsell-add-label]');
      button.disabled = true;
      button.classList.remove('is-error');
      button.classList.add('is-loading');

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: this.rowQuantity(button) }] }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart');
        }

        if (label) {
          label.textContent = 'Added ✓';
          button.classList.add('is-added');
        }
        // Refreshes the cart count, the drawer and the cart page; opens the drawer off the cart page.
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { detail: { open: !this.isCartPage } })
        );
        // The list itself is re-rendered on the cart pages, so this only matters elsewhere.
        setTimeout(() => {
          if (!label) return;
          label.textContent = button.dataset.addLabel;
          button.classList.remove('is-added');
          button.disabled = false;
        }, 2000);
      } catch (error) {
        console.error(error);
        button.classList.add('is-error');
        button.title = error.message;
        button.disabled = false;
      } finally {
        button.classList.remove('is-loading');
      }
    };
  }

  customElements.define('m-upsell-list', MUpsellList);
}
